package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.cssRule
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.api.Async
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * The four screens an asynchronous page draws before, instead of, or after its content.
 *
 * They belong to the design system rather than to the pages that use them because a second
 * application built on this library has the same four states and must show the same sentences. The
 * alternative is an `OpsAsyncFailureState` in the console: a second copy of copy that was argued
 * over once, which then drifts, so the product and the console disagree about what a refusal is.
 *
 * They moved here out of `components/Common.kt`, and `LocaleFlag`, `StateBadge` and `ProgressCell`
 * stayed behind. The line is whether the component knows anything about Lingvo: a heading, an
 * alert, a refusal and a skeleton do not, while a locale flag, a translation state and a progress
 * cell are each shaped by a `dev.lingvo.shared` type that only this product has.
 */

val PageHeaderStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .gap(Space.LG)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("justify-content", "space-between")
            }
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("align-items", "flex-end")
            property("flex-direction", "row")
        }
    }
    /**
     * The mark leads the copy on a desktop and sits above it on a phone.
     *
     * The header stacks into a column below MD, but the row *inside* it — mark, then text — did
     * not, so a 40px mark plus its `Space.MD` gap pushed the breadcrumb, the `H1`, the status
     * badge, the subtitle and the meta row to x≈93 inside a 358px card. The gallery's own header
     * showed all four consequences at 390: the title broke to "Lingvo, in" / "every state", the
     * meta row wrapped after two of three facts, the copy sat on a different left edge from every
     * section heading below it, and the actions slot dropped under the meta row reading as an
     * orphaned control rather than as a header action. One left edge is the rule; this is the row
     * that was breaking it.
     *
     * Written as a rule on the already-registered `PageHeaderStyle` rather than as a style of its
     * own, for the reason `LButtonStyle`'s hit-area rule gives: a new top-level style has to be
     * added by name to every test harness that renders a page header.
     */
    cssRule(" [data-page-header-mark-row]") {
        Modifier.styleModifier { property("flex-direction", "column") }
    }
    cssRule(Breakpoint.MD, " [data-page-header-mark-row]") {
        Modifier.styleModifier { property("flex-direction", "row") }
    }
    /**
     * A header whose actions are all icon buttons puts them beside the title, not under it.
     *
     * Below MD the header stacks, and the actions slot becomes a row of its own. For a slot holding
     * a labelled control that is right — "Add key" is a full-width thing and wants its own line.
     * For a slot holding one 40px icon button it is not: `/app/activity` and
     * `/app/projects/…/data/jobs` each drew a lone refresh glyph on a full-width row of its own,
     * a ~48px band with one glyph at its left edge and 300px of nothing beside it, sitting between
     * the subtitle and the first thing the page is actually about.
     *
     * So an icon-only slot is placed *on the title row* instead: two grid columns, the text block
     * taking what is left of the width and the glyphs sitting at the inline end of the first row.
     * Nothing reserves a band, because there is no second row to reserve one.
     *
     * **Which slot is icon-only is asked of the DOM, not of the caller.** The alternative was a
     * parameter, and 73 call sites pass their actions as a trailing lambda whose contents this
     * composable cannot inspect — a parameter would default to false and fix nothing until every
     * one of them was revisited. [LIconButton] marks itself `data-icon-action`, so "the actions row
     * contains nothing that is not an icon button" is a fact the selector can read. A browser that
     * cannot evaluate `:has()` gets exactly today's layout, which is the right thing to fall back to.
     */
    cssRule(ICON_ONLY_ACTIONS) {
        Modifier.styleModifier {
            property("display", "grid")
            property("grid-template-columns", "minmax(0, 1fr) auto")
            property("align-items", "start")
        }
    }
    cssRule("$ICON_ONLY_ACTIONS [data-page-header-actions]") {
        Modifier.styleModifier { property("justify-self", "end") }
    }
    // Above MD the header is a row again and the grid has nothing left to solve: the actions are
    // already beside the title. Stated explicitly because the rule above out-specifies the plain
    // `Breakpoint.MD` block at the top of this style and would otherwise keep the grid on a desktop.
    cssRule(Breakpoint.MD, ICON_ONLY_ACTIONS) {
        Modifier.styleModifier {
            property("display", "flex")
            property("flex-direction", "row")
            property("align-items", "flex-end")
        }
    }
}

/**
 * "This header's actions slot holds icon buttons and nothing else."
 *
 * Spelled once, because three rules share it and a selector this shape is not one anybody should
 * have to re-derive by eye.
 *
 * **Two `:has()` at the same level, never one inside the other.** The obvious spelling —
 * `:has([data-page-header-actions]:not(:has(> :not(…))))` — is invalid CSS: `:has()` may not be
 * nested inside `:has()`, and Chrome rejects the whole selector, which means the rule silently does
 * nothing and the header keeps the band this fix is about. So it is said as two conditions, joined
 * by `:not()`, which `:has()` *is* allowed inside.
 *
 * **"Nothing else" counts controls, not elements, and that is not a shortcut.** The first version
 * asked whether the slot had any child that was not an icon action, and the answer was always yes:
 * [LIconAction] wraps its button in [LTooltip], and Silk's popover renders a `display: none` `Box`
 * beside the trigger as its "element finder" — a real element, carrying no marker, present on every
 * tooltipped control in the product. Asking about `button, a, input, select, textarea` skips it
 * without special-casing it, and still catches everything this rule must not apply to: a split
 * button's two buttons and a labelled [LButton] are both controls, wrapped or not.
 */
private const val ICON_ONLY_ACTIONS =
    ":has([data-page-header-actions] [data-icon-action])" +
        ":not(:has([data-page-header-actions] " +
        ":is(button, a, input, select, textarea):not([data-icon-action])))"

/**
 * The box a [PageHeader] mark is drawn in.
 *
 * The size lands on the `svg`, not on the box, and that is deliberate. A bare Lucide wrapper carries
 * [LIconStyle]'s own 20px and would ignore a width set on its parent, so the rule sizes the glyph
 * itself to [IconSize.XXL]. Anything that is not an `svg` — a project mark, an icon badge, an
 * uploaded image — draws at whatever size it already decided on, because those components exist
 * precisely to make that decision and a header should not silently overrule them.
 */
val PageHeaderMarkStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "inline-flex")
            property("flex", "0 0 auto")
        }
    }
    cssRule(" > svg") {
        Modifier.width(IconSize.XXL).height(IconSize.XXL)
    }
}

/**
 * The page's `H1`, and the four optional facts that can stand around it.
 *
 * Every slot defaults to null and every one of them is *absent* rather than empty when it is,
 * because 73 call sites pass none of them and must keep rendering the markup they render today —
 * an empty flex child is not free: it takes the column's `Space.XS` gap and pushes the subtitle
 * down by four pixels on every page in the product.
 *
 * - [mark] leads the whole block: the project icon, the provider glyph, the thing being named.
 * - [eyebrow] sits above the title and is where [LBreadcrumbs] goes.
 * - [status] sits inline after the title, so "Checkout · Live" reads as one line rather than as a
 *   heading with a caption. The `H1` moves into a row to make room for it and is otherwise
 *   untouched, which is what keeps the heading level, the font and the `dir="auto"` identical.
 * - [meta] is a `Space.LG`-gapped row of facts under the subtitle — key counts, locales, dates.
 *   Facts, not controls: a control belongs in [actions], where a reader looks for one.
 */
@Composable
fun PageHeader(
    title: String,
    subtitle: String? = null,
    modifier: Modifier = Modifier,
    /**
     * The language `title` is written in, when known — a project or workspace name has no
     * declared language, so callers pass their best guess (a source locale) or leave this null.
     * `dir="auto"` applies either way: it fixes RTL layout without claiming a language.
     */
    titleLang: String? = null,
    mark: (@Composable () -> Unit)? = null,
    eyebrow: (@Composable () -> Unit)? = null,
    status: (@Composable () -> Unit)? = null,
    meta: (@Composable () -> Unit)? = null,
    actions: (@Composable () -> Unit)? = null,
) {
    Row(PageHeaderStyle.toModifier().then(modifier)) {
        if (mark != null) {
            Row(
                // The same `min-width: 0` as the text column below, for the same reason: this row
                // is now the flex item the actions have to share the header with.
                Modifier
                    .gap(Space.MD)
                    .styleModifier { property("min-width", "0") }
                    // Named so `PageHeaderStyle` can turn it into a column below MD; see the rule.
                    .attr("data-page-header-mark-row", "true"),
                verticalAlignment = Alignment.Top,
            ) {
                Div(
                    PageHeaderMarkStyle.toModifier()
                        // Optical, not typographic: the display face leaves space above its caps,
                        // so a mark aligned to the line box's top floats above the letterforms.
                        .padding(top = Space.XS)
                        .toAttrs { attr("data-page-header-mark", "true") },
                ) {
                    mark()
                }
                PageHeaderText(title, subtitle, titleLang, eyebrow, status, meta)
            }
        } else {
            PageHeaderText(title, subtitle, titleLang, eyebrow, status, meta)
        }
        if (actions != null) {
            Row(
                Modifier
                    .gap(Space.SM)
                    .flexWrap(FlexWrap.Wrap)
                    // Named so `PageHeaderStyle` can ask what is inside it; see [ICON_ONLY_ACTIONS].
                    .attr("data-page-header-actions", "true"),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                actions()
            }
        }
    }
}

@Composable
private fun PageHeaderText(
    title: String,
    subtitle: String?,
    titleLang: String?,
    eyebrow: (@Composable () -> Unit)?,
    status: (@Composable () -> Unit)?,
    meta: (@Composable () -> Unit)?,
) {
    // A flex item's default min-width is auto, not 0 — it will not shrink below its content's
    // intrinsic width. A long project name pushed the actions row out of the header instead of
    // wrapping, because the row had nowhere to shrink to.
    Column(
        Modifier
            .gap(Space.XS)
            .styleModifier { property("min-width", "0") }
            .attr("data-page-header-text", "true"),
    ) {
        if (eyebrow != null) {
            Div(
                Modifier
                    .styleModifier { property("min-width", "0") }
                    .toAttrs { attr("data-page-header-eyebrow", "true") },
            ) {
                eyebrow()
            }
        }
        if (status != null) {
            Row(
                Modifier.gap(Space.MD).flexWrap(FlexWrap.Wrap),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                PageHeaderTitle(title, titleLang)
                Span(Modifier.toAttrs { attr("data-page-header-status", "true") }) {
                    status()
                }
            }
        } else {
            PageHeaderTitle(title, titleLang)
        }
        if (!subtitle.isNullOrBlank()) {
            P(
                Modifier
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .fontSize(TypeScale.BodySmall)
                    .lineHeight(TypeScale.BodyLineHeight)
                    .margin(0.px)
                    .toAttrs(),
            ) {
                Text(subtitle)
            }
        }
        if (meta != null) {
            Row(
                Modifier
                    .gap(Space.LG)
                    .flexWrap(FlexWrap.Wrap)
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .fontSize(TypeScale.Small)
                    .lineHeight(TypeScale.BodyLineHeight)
                    .attr("data-page-header-meta", "true"),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                meta()
            }
        }
    }
}

@Composable
private fun PageHeaderTitle(title: String, titleLang: String?) {
    H1(
        Modifier
            .displayFont()
            .color(ColorMode.current.toSitePalette().textPrimary)
            .fontSize(TypeScale.Heading)
            .fontWeight(700)
            .lineHeight(TypeScale.DisplayLineHeight)
            .margin(0.px)
            .toAttrs {
                attr("dir", "auto")
                titleLang?.let { attr("lang", it) }
            },
    ) {
        Text(title)
    }
}

@Composable
fun ErrorState(
    message: String,
    modifier: Modifier = Modifier,
    title: String = t("ds.ds.lstates.couldnt_load", "Couldn't load this content"),
    onRetry: (() -> Unit)? = null,
) {
    LAlert(
        title = title,
        description = message,
        modifier = modifier,
        variant = LAlertVariant.DANGER,
        action = onRetry?.let { retry ->
            {
                LButton(
                    label = t("ds.ds.lstates.retry", "Retry"),
                    onClick = retry,
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                )
            }
        },
    )
}

/**
 * A failed load, told apart by what actually failed.
 *
 * **A refusal is not an error and must not offer Retry.** Every failed load used to render the same
 * red alert with the same button, so a translator opening a billing route was told "we couldn't read
 * your plan" — which sounds like a fault, invites them to press Retry, and fails identically every
 * time. It is not a fault: they are not allowed to see it, and the honest screen says so and offers
 * them somewhere to go instead.
 *
 * The same for a deleted project. "Couldn't load this content · Retry" is the product looking broken
 * on behalf of a thing that is simply gone.
 *
 * Only a genuine failure — a 5xx, a dropped connection, a status we never got — keeps the alert and
 * the button, because only there is retrying a reasonable thing to do.
 *
 * One function rather than a branch on each page, because the branch is the part that gets forgotten:
 * eleven surfaces render a failed load and the specs give distinct copy for three of them.
 */
@Composable
fun AsyncFailureState(
    failure: Async.Failure,
    modifier: Modifier = Modifier,
    /**
     * What this screen is about, for the refusal sentence: "usage", "billing", "this project".
     *
     * Defaulted so a caller that has not thought about it still reads as a sentence. A page whose
     * refusal is worth wording precisely should say so — "You don't have access to usage" is better
     * than the default, and both are far better than "Couldn't load this content".
     */
    subject: String = t("ds.ds.lstates.this_page", "this page"),
    /** Page-specific copy for a genuine fault. Falls back to whatever the failure carried. */
    faultMessage: String? = null,
    onRetry: (() -> Unit)? = null,
) {
    when {
        // No action on either. `LEmptyState` requires the slot, and an empty one is the right
        // content here: the only honest next step for a refusal is a conversation with an admin, and
        // for a missing thing it is the navigation the reader already has. A button that re-ran the
        // same refused request would be the Retry this function exists to remove.
        failure.isRefusal -> LEmptyState(
            title = tf("ds.ds.lstates.no_access_to", "You don't have access to {subject}", "subject" to subject),
            description = t(
                "ds.ds.lstates.ask_an_owner_or_admin",
                "Ask an owner or admin of this workspace if you need it.",
            ),
            modifier = modifier,
            icon = LEmptyStateIcon.FORBIDDEN,
        ) {}

        failure.isMissing -> LEmptyState(
            title = t("ds.ds.lstates.that_isnt_here_any_more", "That isn't here any more"),
            description = t(
                "ds.ds.lstates.may_have_been_deleted",
                "It may have been deleted, or the link may be wrong.",
            ),
            modifier = modifier,
            icon = LEmptyStateIcon.HELP,
        ) {}

        // Genuine fault: the alert and the button, because retrying is a reasonable thing to do.
        // `faultMessage` lets a page keep copy that is only true here — a billing page saying
        // "nothing has been charged" is the reassurance its reader actually wants, and would be a
        // strange thing to say on a QA page.
        else -> ErrorState(
            // `readable()`, not `message`: the server writes its refusals as English sentences and
            // they reach a reader whose whole interface is in another language. The error's `code`
            // is the only stable thing in the pair, so a catalogue entry keys on that and the
            // server's own wording is the fallback until one exists.
            message = faultMessage ?: failure.readable(),
            modifier = modifier,
            onRetry = onRetry,
        )
    }
}

/**
 * **One live region, not one per bar.**
 *
 * [LSkeleton] announces itself as a `status`, which is right for a single bar standing in for a
 * value and unbearable for a stack of them: this composable draws `rows + 1`, so an eight-row
 * loading state was eight simultaneous announcements of the same sentence, and a screen reader read
 * "Loading activity" nine times before the page had said anything. The bars are decorative — the
 * information is "this is loading", and one sentence carries it.
 *
 * The workspace overview worked around this locally by hiding its own bars and speaking once; that
 * fix belongs here, where every caller gets it.
 */
@Composable
fun LoadingState(
    modifier: Modifier = Modifier,
    rows: Int = 3,
    label: String = t("ds.ds.lstates.loading_content", "Loading content"),
) {
    Span(LVisuallyHiddenStyle.toModifier().toAttrs { attr("role", "status") }) {
        Text(label)
    }
    Column(
        modifier
            .width(100.percent)
            .gap(Space.MD)
            .padding(topBottom = Space.SM)
            .attr("aria-hidden", "true"),
    ) {
        LSkeleton(
            width = 42.percent,
            height = 24.px,
            shape = LSkeletonShape.TEXT,
            label = null,
        )
        repeat(rows.coerceAtLeast(1)) {
            LSkeleton(height = 56.px, label = null)
        }
    }
}
